import { MapPin, Calendar, Gauge } from "lucide-react";
import { ui, SECTION_TAG_LABELS, formatDate } from "../lib/ui";

function MatchBadge({ value }) {
  if (!value) return null;
  const color =
    value === "Yes" ? "bg-[#8B1A1A] text-[#F5EAE0]" :
    value === "No" ? "bg-[#E8DCC8] text-[#671813]" :
    "bg-[#F3E5D8] text-[#8B1A1A]";
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>Predicted: {value}</span>;
}

export default function ReportCard({ report }) {
  const r = report;
  const tags = r.section_tags || [];
  const SHORT = { eo_a: "EO·A", eo_b: "EO·B", ee_a: "EE·A", ee_b: "EE·B" };
  const cornerTopics = [
    ["eo_a", r.eo?.section_a],
    ["eo_b", r.eo?.section_b],
    ["ee_a", r.ee?.section_a],
    ["ee_b", r.ee?.section_b],
  ]
    .filter(([, s]) => s?.reported && s?.topic_number != null)
    .map(([code, s]) => ({ code, number: s.topic_number }));

  return (
    <article className={ui.card + " p-6 flex flex-col gap-4"} data-testid="report-card-container">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 text-sm text-[#5A4441]">
          <span className="inline-flex items-center gap-1.5"><Calendar size={14} className="text-[#8B1A1A]" />{formatDate(r.exam_date)}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#8B1A1A]" />{r.city}{r.country ? `, ${r.country}` : ""}</span>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {cornerTopics.length > 0 && (
            <div className="flex flex-wrap justify-end gap-1.5" data-testid="report-card-corner-topics">
              {cornerTopics.map((t) => (
                <span
                  key={t.code}
                  className="inline-flex items-center gap-1 bg-[#8B1A1A] text-[#F5EAE0] font-semibold text-xs px-2 py-0.5 rounded-full"
                  title={`${SECTION_TAG_LABELS[t.code]} · Topic #${t.number}`}
                  data-testid={`report-card-corner-topic-${t.code}`}
                >
                  {SHORT[t.code]} #{t.number}
                </span>
              ))}
            </div>
          )}
          {r.difficulty && (
            <span className="inline-flex items-center gap-1.5 text-xs text-[#671813]">
              <Gauge size={14} /> Difficulty {r.difficulty}/5
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {tags.map((t) => (
          <span key={t} className={ui.pill}>{SECTION_TAG_LABELS[t] || t}</span>
        ))}
      </div>

      <div className="space-y-3 text-[#210201] text-sm leading-relaxed">
        {r.eo?.section_a?.reported && r.eo?.section_a?.topic && (
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-semibold text-[#671813]">Expression orale · A</span>
              {r.eo.section_a.topic_number != null && <span className="inline-flex items-center justify-center bg-[#8B1A1A] text-[#F5EAE0] font-semibold text-xs px-2 py-0.5 rounded-full">#{r.eo.section_a.topic_number}</span>}
              <MatchBadge value={r.eo.section_a.prediction_match} />
            </div>
            <p>{r.eo.section_a.topic}</p>
          </div>
        )}
        {r.eo?.section_b?.reported && r.eo?.section_b?.topic && (
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-semibold text-[#671813]">Expression orale · B</span>
              {r.eo.section_b.topic_number != null && <span className="inline-flex items-center justify-center bg-[#8B1A1A] text-[#F5EAE0] font-semibold text-xs px-2 py-0.5 rounded-full">#{r.eo.section_b.topic_number}</span>}
              <MatchBadge value={r.eo.section_b.prediction_match} />
            </div>
            <p>{r.eo.section_b.topic}</p>
          </div>
        )}
        {r.eo?.followup && (
          <p className="text-[#5A4441] italic">Follow-up / objections: {r.eo.followup}</p>
        )}
        {r.ee?.section_a?.reported && r.ee?.section_a?.prompt && (
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-semibold text-[#671813]">Expression écrite · A</span>
              {r.ee.section_a.topic_number != null && <span className="inline-flex items-center justify-center bg-[#8B1A1A] text-[#F5EAE0] font-semibold text-xs px-2 py-0.5 rounded-full">#{r.ee.section_a.topic_number}</span>}
              <MatchBadge value={r.ee.section_a.prediction_match} />
            </div>
            <p>{r.ee.section_a.prompt}</p>
          </div>
        )}
        {r.ee?.section_b?.reported && r.ee?.section_b?.prompt && (
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-semibold text-[#671813]">Expression écrite · B</span>
              {r.ee.section_b.topic_number != null && <span className="inline-flex items-center justify-center bg-[#8B1A1A] text-[#F5EAE0] font-semibold text-xs px-2 py-0.5 rounded-full">#{r.ee.section_b.topic_number}</span>}
              <MatchBadge value={r.ee.section_b.prediction_match} />
            </div>
            <p>{r.ee.section_b.prompt}</p>
          </div>
        )}
        {(r.co && r.co.matched_count != null) && (
          <p><span className="font-semibold text-[#671813]">Compréhension orale:</span> {r.co.matched_count} questions matched predictions{r.co.source ? ` · ${r.co.source}` : ""}</p>
        )}
        {(r.ce && r.ce.matched_count != null) && (
          <p><span className="font-semibold text-[#671813]">Compréhension écrite:</span> {r.ce.matched_count} questions matched predictions{r.ce.source ? ` · ${r.ce.source}` : ""}</p>
        )}
        {r.note && (
          <p className="text-[#5A4441] border-l-2 border-[#E8DCC8] pl-3">{r.note}</p>
        )}
      </div>
    </article>
  );
}
